import { useState, useEffect } from 'react';
import { Box, Stack, TextField, Button, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem, alpha } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAppSelector } from '../../../hooks/useAppStore';
import { useUpdateUserMutation } from '../../../store/api/usersApi';
import { useGetMyWhatsappStatusQuery } from '../../../store/api/usersApi';
import { useAppSnackbar } from '../../../hooks/useAppSnackbar';
import { COUNTRIES, parsePhone, codigoTelefonicoDeIso } from '../../../utils/phoneCountries';
import { useGetPaisDelTenantQuery } from '../../../store/api/monedasApi';

const WA_GREEN = '#25D366';

/**
 * Paso del onboarding para activar WhatsApp. El número viene prellenado del
 * registro y es editable: el agente lo confirma/edita y guarda — queda
 * reconocido por Gustavo al instante. Abajo, la tarjeta para escribirle.
 */
export function WhatsappStep() {
  const user = useAppSelector((s) => s.auth.user);
  const { data: status } = useGetMyWhatsappStatusQuery();
  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();
  const snackbar = useAppSnackbar();
  const [local, setLocal] = useState('');
  // El prefijo arranca en el del país del tenant (PY → +595); un número ya guardado manda.
  const { data: pais } = useGetPaisDelTenantQuery();
  const prefijoDelPais = codigoTelefonicoDeIso(pais?.countryIso);
  const [countryCode, setCountryCode] = useState(prefijoDelPais);
  useEffect(() => { if (pais && !local) setCountryCode(prefijoDelPais); }, [pais]); // eslint-disable-line react-hooks/exhaustive-deps
  const [touched, setTouched] = useState(false);

  // Prellena con el número del registro (país + local, editable) al cargar el estado.
  useEffect(() => {
    if (!touched && status?.myPhone) {
      const { countryCode: cc, local: loc } = parsePhone(status.myPhone, prefijoDelPais);
      setCountryCode(cc);
      setLocal(loc.replace(/\D/g, ''));
    }
  }, [status?.myPhone, touched]);

  const handleSave = async () => {
    const digits = local.replace(/\D/g, '');
    if (digits.length < 6) {
      snackbar('Ingresá un número válido', 'warning');
      return;
    }
    const phone = countryCode + digits.replace(/^0/, '');
    try {
      await updateUser({ id: user!._id, body: { phone } }).unwrap();
      snackbar('¡Número guardado! Gustavo ya te reconoce.', 'success');
    } catch (err: any) {
      snackbar(err?.data?.error ?? 'No se pudo guardar el número', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 440, mx: 'auto', textAlign: 'left' }}>
      <Box
        sx={{
          mb: 2, p: 2, borderRadius: 2,
          border: `1px solid ${alpha(WA_GREEN, 0.3)}`,
          bgcolor: alpha(WA_GREEN, 0.05),
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25, textAlign: 'center' }}>
          Confirmá tu número de WhatsApp
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 108, flexShrink: 0 }}>
            <InputLabel>País</InputLabel>
            <Select
              value={countryCode}
              label="País"
              onChange={(e) => { setTouched(true); setCountryCode(e.target.value); }}
              disabled={saving}
              MenuProps={{ sx: { zIndex: 2400 } }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.iso ?? c.code} value={c.code}>{c.flag}&nbsp;{c.code}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            value={local}
            onChange={(e) => { setTouched(true); setLocal(e.target.value); }}
            placeholder="99 123 456"
            size="small"
            fullWidth
            disabled={saving}
            inputProps={{ inputMode: 'tel' }}
          />
          <Button
            onClick={handleSave}
            disabled={saving || !local.trim()}
            variant="contained"
            startIcon={saving ? <CircularProgress size={15} sx={{ color: '#0A0A0A' }} /> : <WhatsAppIcon />}
            sx={{
              flexShrink: 0, textTransform: 'none', fontWeight: 800,
              bgcolor: WA_GREEN, color: '#0A0A0A',
              '&:hover': { bgcolor: '#1FB855' },
            }}
          >
            Guardar
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
